/**
 * StatsPanel — read-only modal listing the player's current effective stats
 * (mining rate + beam breakdown, range, ship speed, yield, drones, progress).
 */
import type { ReactNode } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { t, useLang } from "@/i18n";

interface Props {
  snap: HudSnapshot;
  onClose: () => void;
}

export default function StatsPanel({ snap, onClose }: Props) {
  useLang();
  const s = snap.stats;
  const dronesUnlocked = s.hangarLevel > 0;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Stats"
      >
        <div className="upg-head">
          <div className="upg-head-title">📊 {t("Stats")}</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="stats-body">
          <Section title={t("Progress")}>
            <Row k={t("Level")} v={`${snap.level}`} />
            <Row k="XP" v={`${Math.floor(snap.exp)} / ${snap.expToNext}`} />
            <Row k={t("Skill Points")} v={`${snap.skillPoints}`} hi={snap.skillPoints > 0} />
          </Section>

          <Section title={t("Hull mining")}>
            <Row k={t("Mining rate")} v={`${s.miningRate.toFixed(1)} /sec`} hi />
            <Row
              k={t("Beams")}
              v={`${s.beamCount} × ${s.beamRate.toFixed(1)}/sec`}
            />
            <Row k={t("Mining range")} v={`${Math.round(s.miningRange)}`} />
          </Section>

          <Section title={t("Ship")}>
            <Row k={t("Speed")} v={`${Math.round(s.shipSpeed)} px/s`} />
            <Row k={t("Mineral yield")} v={`×${s.yieldMul.toFixed(2)}`} />
          </Section>

          <Section title={t("Drones")}>
            {dronesUnlocked ? (
              <>
                <Row k={t("Max drones")} v={`${s.hangarLevel}`} />
                <Row k={t("Deploy range")} v={`${Math.round(s.droneRange)}`} />
                <Row k={t("Drone speed")} v={`${Math.round(s.droneSpeed)} px/s`} />
                <Row k={t("Drone capacity")} v={`${Math.round(s.droneCargo)}`} />
                <Row k={t("Drone mining")} v={`${s.droneRate.toFixed(1)} /sec each`} />
              </>
            ) : (
              <Row k={t("Status")} v={t("Locked — reach Mk II")} />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="stats-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

function Row({ k, v, hi }: { k: string; v: string; hi?: boolean }) {
  return (
    <div className="stat-row">
      <span className="k">{k}</span>
      <span className={hi ? "v hi" : "v"}>{v}</span>
    </div>
  );
}
